import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NovaUnifiedPageBar, NovaUnifiedInlineAnalysis, NovaUnifiedMediumHeader } from '../../components/NovaUnified';
import SectionHeader from '../../components/SectionHeader';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { layoutMobileMediaQuery, introCardKeyline, demoSectionCardStackStyle, DEMO_MINI_CARD_GRID_GAP_PX } from '../../designSystem';
import { TTC_MAYA_CYCLE, OURA_SESSION_KEY } from '../../data/ttcMyCycleMock';

const FONT = "'Plus Jakarta Sans', -apple-system, sans-serif";

function CycleSectionHeader({ title, descriptor, trailing }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <SectionHeader title={title} descriptor={descriptor} />
      </div>
      {trailing}
    </div>
  );
}

function OuraRingIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden style={{ flexShrink: 0 }}>
      <circle cx="10" cy="10" r="8" fill="none" stroke="var(--purple-m)" strokeWidth="1.2" />
      <circle cx="10" cy="10" r="5.2" fill="none" stroke="var(--purple-d)" strokeWidth="1" />
      <circle cx="10" cy="10" r="2.5" fill="var(--purple-l)" stroke="var(--purple)" strokeWidth="0.8" />
    </svg>
  );
}

const CYCLE_DAY_TODAY = 14;

/** Cycle calendar day cell — matches MY CALENDAR spec (Maya · day 14). */
function getCycleDayVisuals(day) {
  if (day === CYCLE_DAY_TODAY) {
    return { size: 36, bg: 'var(--purple)', color: '#fff', border: 'none', fontWeight: 700 };
  }
  if (day >= 1 && day <= 5) {
    return { size: 32, bg: '#EEEDFE', color: 'var(--muted)', border: 'none', fontWeight: 600 };
  }
  if (day >= 13 && day <= 15) {
    return {
      size: 32,
      bg: 'var(--purple-l)',
      color: 'var(--purple-d)',
      border: '1px solid var(--purple-d)',
      fontWeight: 600,
    };
  }
  if (day >= 16 && day <= 27) {
    return { size: 32, bg: '#F1EFE8', color: 'var(--muted)', border: 'none', fontWeight: 600 };
  }
  if (day > 5 && day < CYCLE_DAY_TODAY) {
    return { size: 32, bg: 'var(--purple-m)', color: '#fff', border: 'none', fontWeight: 600 };
  }
  return { size: 32, bg: 'transparent', color: 'var(--muted)', border: '1px solid var(--border)', fontWeight: 600 };
}

/**
 * TTC demo — My Cycle. Layout/tokens align with TTCHomePage + chapter purple system.
 * Oura: demo toggles session flag; production would OAuth to cloud.ouraring.com (API V2)
 * and map daily_readiness temperature data into BBT row (token stored server-side per user).
 */
export default function TTCMyCyclePage({ navigate }) {
  const nav = navigate || (() => {});
  const location = useLocation();
  const m = useMediaQuery(layoutMobileMediaQuery);
  const demoSlug = location.pathname.match(/^\/demo\/([^/]+)/)?.[1] || 'trying-to-conceive';
  const demoBase = `/demo/${demoSlug}`;

  const [ouraConnected, setOuraConnected] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(OURA_SESSION_KEY) === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (ouraConnected) window.sessionStorage.setItem(OURA_SESSION_KEY, '1');
  }, [ouraConnected]);

  const connectOuraDemo = useCallback(() => {
    setOuraConnected(true);
  }, []);

  const card = {
    background: 'var(--card)',
    border: '0.5px solid var(--border)',
    borderRadius: 'var(--r)',
    padding: '16px 18px',
  };

  const rowBorder = { borderBottom: '0.5px solid var(--sand-2)' };

  const mock = TTC_MAYA_CYCLE;

  return (
    <div style={{ fontFamily: FONT, width: '100%', boxSizing: 'border-box' }}>
      {/* Section 1 — Nova check-in (unified block — same as fertility demo) */}
      <NovaUnifiedPageBar
        layout="stacked"
        contextPill="Daily check-in"
        body={mock.novaCheckIn}
        actionLabel="Open Nova →"
        onAction={() => nav('/nova')}
      />

      <div style={demoSectionCardStackStyle}>
      {/* Section 2 — two columns */}
      <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1fr', gap: 14 }}>
        {/* Today's log */}
        <div style={card}>
          <CycleSectionHeader
            title="Today's log"
            descriptor="BBT, OPK, and cervical mucus — one place to log what happened today."
            trailing={
              <button
                type="button"
                onClick={() => nav(`${demoBase}/health`)}
                style={{
                  fontFamily: FONT,
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--purple-d)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textTransform: 'none',
                  letterSpacing: 0,
                }}
              >
                See history →
              </button>
            }
          />

          {/* BBT row */}
          <div style={{ ...rowBorder, paddingBottom: 12, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--purple)', marginTop: 5, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>BBT temperature</span>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--purple-d)' }}>{mock.todayLog.bbt.value}</span>
                      {ouraConnected ? (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '2px 7px',
                            borderRadius: 999,
                            background: 'var(--purple-l)',
                            color: 'var(--purple-dk)',
                          }}
                        >
                          Oura auto-synced
                        </span>
                      ) : null}
                    </div>
                    {mock.todayLog.bbt.sub ? (
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{mock.todayLog.bbt.sub}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* OPK row */}
          <div style={{ ...rowBorder, paddingBottom: 12, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--purple-d)', marginTop: 5, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>OPK result</span>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--purple-d)' }}>{mock.todayLog.opk.value}</span>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{mock.todayLog.opk.sub}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CM row */}
          <div style={{ paddingBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--border)', marginTop: 5, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>Cervical mucus</span>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--muted)' }}>{mock.todayLog.cm.value}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Oura prompt or connected */}
          {!ouraConnected ? (
            <div
              style={{
                marginTop: 14,
                background: 'var(--purple-l)',
                borderRadius: 8,
                padding: '9px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <OuraRingIcon />
              <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: 'var(--purple-d)', lineHeight: 1.45 }}>
                Have an Oura Ring? Connect it and BBT logs automatically every morning.
              </div>
              <button
                type="button"
                onClick={connectOuraDemo}
                style={{
                  fontFamily: FONT,
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--purple)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Connect →
              </button>
            </div>
          ) : (
            <div
              style={{
                marginTop: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <OuraRingIcon />
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>Oura Ring</span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 999,
                  background: '#E1F5EE',
                  color: '#085041',
                }}
              >
                Live sync
              </span>
              <div style={{ width: '100%', fontSize: 11, color: 'var(--purple-d)', lineHeight: 1.45 }}>
                BBT and sleep sync every morning automatically
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => nav('/nova')}
              style={{
                fontFamily: FONT,
                fontSize: 12,
                fontWeight: 700,
                color: '#fff',
                background: 'var(--purple)',
                border: 'none',
                borderRadius: 999,
                padding: '8px 14px',
                cursor: 'pointer',
              }}
            >
              + Log CM / OPK
            </button>
            <button
              type="button"
              onClick={() => nav('/nova')}
              style={{
                fontFamily: FONT,
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--purple-d)',
                background: 'var(--white)',
                border: '1px solid var(--purple-m)',
                borderRadius: 999,
                padding: '8px 14px',
                cursor: 'pointer',
              }}
            >
              Edit BBT
            </button>
          </div>
        </div>

        {/* Cycle status */}
        <div style={card}>
          <CycleSectionHeader title="Cycle status" descriptor="Phase, fertile window, and key dates Nova uses for timing and check-ins." />
          {mock.cycleStatusRows.map((row, i) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 12,
                padding: '10px 0',
                borderBottom: i < mock.cycleStatusRows.length - 1 ? '0.5px solid var(--sand-2)' : 'none',
              }}
            >
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{row.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: row.valueColor || 'var(--text)' }}>{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Section 3 — BBT chart (gradient band matches fertility /demo/fertility/myjourney journey card) */}
      <div
        style={{
          background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))',
          ...introCardKeyline,
          padding: '22px 26px',
          boxSizing: 'border-box',
        }}
      >
        <CycleSectionHeader
          title="BBT this cycle"
          descriptor="Thermal pattern across your cycle — confirms ovulation after your surge."
          trailing={
            <button
              type="button"
              onClick={() => nav(`${demoBase}/health`)}
              style={{
                fontFamily: FONT,
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--purple-d)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              Full chart →
            </button>
          }
        />
        <div style={{ display: 'flex', height: 24, borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
          {mock.bbtPhaseStrip.map((seg) => (
            <div
              key={seg.label}
              style={{
                flex: seg.flex,
                background: seg.bg,
                color: seg.color,
                fontSize: seg.fontSize ?? 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                letterSpacing: '0.02em',
              }}
            >
              {seg.label}
            </div>
          ))}
        </div>
        <div
          style={{
            background: 'var(--sand)',
            borderRadius: 8,
            height: 70,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 3,
            padding: '8px 12px',
            boxSizing: 'border-box',
          }}
        >
          {mock.bbtBarFracs.map((h, idx) => (
            <div
              key={`bbt-${idx}`}
              style={{
                flex: 1,
                height: `${h * 100}%`,
                background: 'var(--purple-m)',
                borderRadius: 2,
                minHeight: 4,
              }}
            />
          ))}
          <div
            style={{
              flex: 1,
              height: '82%',
              background: 'var(--purple)',
              borderRadius: 2,
              border: '1.5px solid var(--purple-d)',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: m ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: DEMO_MINI_CARD_GRID_GAP_PX,
            marginTop: 14,
          }}
        >
          {mock.bbtStats.map((s) => (
            <div key={s.label}>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{s.label}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: s.valueColor || 'var(--text)', marginTop: 2 }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 4 — Cycle intelligence */}
      <div style={card}>
        <CycleSectionHeader
          title="Cycle intelligence"
          descriptor="Patterns Nova spots across months — not single-day noise."
          trailing={
            <button
              type="button"
              onClick={() => nav(`${demoBase}/cycle-intelligence`)}
              style={{
                fontFamily: FONT,
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--purple-d)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              Full analysis →
            </button>
          }
        />
        {mock.intelligencePatterns.map((p, i) => (
          <div
            key={p.title}
            style={{
              display: 'flex',
              gap: 12,
              padding: '12px 0',
              borderBottom: i < mock.intelligencePatterns.length - 1 ? '0.5px solid var(--sand-2)' : 'none',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: p.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              {p.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: p.titleColor }}>{p.title}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5, marginTop: 4 }}>{p.sub}</div>
              {p.flag ? (
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: 8,
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 999,
                    background: p.flag.bg,
                    color: p.flag.color,
                  }}
                >
                  {p.flag.label}
                </span>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {/* MY CALENDAR */}
      <div style={card}>
        <CycleSectionHeader
          title="My calendar"
          descriptor="Cycle days and what’s coming — peak window, TWW, and test day."
          trailing={
            <button
              type="button"
              onClick={() => nav(`${demoBase}/events`)}
              style={{
                fontFamily: FONT,
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--purple-d)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              Add event →
            </button>
          }
        />
        <div
          style={{
            display: 'flex',
            gap: DEMO_MINI_CARD_GRID_GAP_PX,
            overflowX: 'auto',
            paddingBottom: 6,
            marginBottom: 14,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => {
            const v = getCycleDayVisuals(d);
            return (
              <div
                key={d}
                style={{
                  width: v.size,
                  height: v.size,
                  minWidth: v.size,
                  minHeight: v.size,
                  borderRadius: '50%',
                  background: v.bg,
                  color: v.color,
                  border: v.border,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: d === CYCLE_DAY_TODAY ? 13 : 11,
                  fontWeight: v.fontWeight,
                  flexShrink: 0,
                }}
              >
                {d}
              </div>
            );
          })}
        </div>
        <div>
          {[
            {
              dot: 'var(--purple)',
              title: 'Peak fertile window ends',
              meta: 'Tomorrow · Day 15',
            },
            {
              dot: 'var(--purple-m)',
              title: 'TWW begins',
              meta: 'Apr 16 · Day 16',
            },
            {
              dot: 'var(--border)',
              title: 'Test day',
              meta: 'Apr 26 · Day 26 · est.',
            },
          ].map((ev, i) => (
            <div
              key={ev.title}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 0',
                borderBottom: i < 2 ? '0.5px solid var(--sand-2)' : 'none',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: ev.dot, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--text)', minWidth: 0 }}>{ev.title}</span>
              <span style={{ fontSize: 11, color: 'var(--muted)', flexShrink: 0, textAlign: 'right' }}>{ev.meta}</span>
            </div>
          ))}
        </div>
      </div>

      {/* COMFORT PLAN */}
      <div style={card}>
        <CycleSectionHeader
          title="Comfort plan"
          descriptor="Small actions that help today — based on what you logged and your cycle phase."
          trailing={
            <button
              type="button"
              onClick={() => nav('/nova')}
              style={{
                fontFamily: FONT,
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--purple-d)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              Customize →
            </button>
          }
        />
        <p style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.45, margin: '0 0 14px' }}>
          Based on your logged symptoms today and your cycle history.
        </p>
        {[
          {
            icon: '💧',
            iconBg: 'var(--purple-l)',
            title: 'Stay hydrated',
            sub: 'You logged mild bloating today. 2–3L water helps reduce fluid retention during your fertile window.',
            tag: { label: 'Today', bg: 'var(--purple-l)', color: 'var(--purple-d)' },
          },
          {
            icon: '🚶',
            iconBg: 'var(--purple-l)',
            title: 'Light movement today',
            sub: 'A 20-minute walk supports circulation during peak ovulation without adding stress to your body.',
            tag: null,
          },
          {
            icon: '😴',
            iconBg: '#FEF0EB',
            title: 'Prioritize sleep tonight',
            sub: 'Your Oura data shows HRV dipped last night. Quality sleep in your fertile window supports hormone balance.',
            tag: { label: 'Oura insight', bg: '#FEF0EB', color: '#AD3F1A' },
          },
          {
            icon: '🧘',
            iconBg: 'var(--purple-l)',
            title: 'Reduce stress if possible',
            sub: 'High cortisol can delay or suppress ovulation. Even 10 minutes of stillness today counts.',
            tag: null,
          },
        ].map((row, i) => (
          <div
            key={row.title}
            style={{
              display: 'flex',
              gap: 12,
              padding: '12px 0',
              borderBottom: i < 3 ? '0.5px solid var(--sand-2)' : 'none',
            }}
          >
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                background: row.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {row.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{row.title}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5, marginTop: 4 }}>{row.sub}</div>
              {row.tag ? (
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: 8,
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 999,
                    background: row.tag.bg,
                    color: row.tag.color,
                  }}
                >
                  {row.tag.label}
                </span>
              ) : null}
            </div>
          </div>
        ))}
        <div style={{ marginTop: 14 }}>
          <NovaUnifiedInlineAnalysis compact style={{ marginTop: 0 }}>
            <NovaUnifiedMediumHeader contextPill="Guidance" />
            <p style={{ fontSize: 12, color: '#4b1528', lineHeight: 1.5, margin: 0 }}>
              These suggestions are based on Day 14 of your cycle, your logged symptoms, and your Oura sleep data from last night.
              Customize any time.
            </p>
          </NovaUnifiedInlineAnalysis>
        </div>
      </div>
      </div>
    </div>
  );
}
